<script setup lang="ts">
import { computed, onMounted, reactive, ref, h, watch, nextTick } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import max from 'lodash/max';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { helpers, integer, required, minValue } from '@vuelidate/validators';
import { CRUISE, LANG_ENGLISH, LANG_FRENCH, ROUNDTRIP, OPENJAW, ONEWAY } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useCityStore } from '@/store/CityStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useCruiseStore } from '@/store/CruiseStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useDestinationStore } from '@/store/DestinationStore';
import { useHotelStore } from '@/store/HotelStore';
import { useTabComposable } from './useTabComposable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import CruiseFormOption from './CruiseQuoteForm/CruiseFormOption.vue';
import RoomCategoryFetchingModal from './RoomCategoryFetchingModal.vue';
import SubmitNotificationModal from '@/components/modal/SubmitNotificationModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';
import AddFlightConfirmModal from '@/components/modal/AddFlightConfirmModal.vue';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { getTextVariant } from '@/utils/stringUtils';

interface Props {
  instanceForm?: object;
  advancedForm: object;
  avgPaxRoomData: number;
  selectedLanguage: string;
  isValidation: boolean;
  quoteIntro: object;
  calendarData?: object;
}
const props = withDefaults(defineProps<Props>(), {
  isValidation: false
});

const emits = defineEmits<{
  submit: [];
  clear: [];
}>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const { roomCategoryFetchingModalControl, getUserParam, getUserQuote, renderOriginQuote, renderRoomName, getQuoteDepartureData, handleSetLimit, removeFromEnd, getLimitDate, validateBlackoutDate } = useTabComposable();
const submitQuoteStore = useSubmitQuoteStore();
const cruiseStore = useCruiseStore();
const viewQuoteStore = useViewQuoteStore();
const destinationStore = useDestinationStore();
const hotelStore = useHotelStore();
const airManagementStore = useAirManagementStore();
const adminQuoteStore = useAdminQuoteStore();
const cityStore = useCityStore();
const adminSetupStore = useAdminSetupStore();
const listCabinCategory: any = ref([]);
const currentLanguage = getCurrentLocale();
const maxOptionConfig = ref<number | null>();
const maxHotelConfig = ref<number | null>();
const maxRoomConfig = ref<number | null>();
const maxCategoryConfig = ref<number | null>();
const isClearAgencyInfo = ref(false);
const isRoundtrip = ref(false);
const isOpenJaw = ref(false);
const isOneWay = ref(false);
const limitCalendar = ref({});
const minDateRangeValue = ref(new Date(DateTime.utc().plus({ days: 60 })));
const maxDateRangeValue = ref<(Date | undefined)[]>([]);

const flightTypesOption = [{ type: 'Outbound' }, { type: 'Inbound' }];
const serviceFlight = [{ type: 'Economy' }, { type: 'Business' }];

const initHotelOption = () => {
  return {
    hotelName: '',
    rooms: [
      {
        room: ''
      }
    ]
  };
};

const initHotel = () => {
  return [
    {
      destination: '',
      dateRange: [],
      duration: '',
      adults: 0,
      numOfRooms: 0,
      children: 0,
      hotel: [initHotelOption()]
    }
  ];
};

const initFlight = () => {
  return [
    {
      gateway: '',
      destination: '',
      dateRange: [],
      depDate: null,
      duration: '0',
      adults: 0,
      children: 0,
      service: serviceFlight,
      directFlight: false,
      businessAdults: 0,
      businessChildren: 0,
      isEconomy: true,
      isBusiness: false,
      returningFrom: '',
      returningTo: '',
      tripTypeSelected: ROUNDTRIP
    }
  ];
};

const initCruiseData = () => {
  return {
    destination: '',
    lineName: '',
    shipName: '',
    sailingName: '',
    numberOfCabins: '',
    ship: '',
    shipId: '',
    cruiseLine: '',
    sailDate: null,
    retDate: null,
    duration: '',
    adults: 0,
    children: 0,
    diningPreference: '',
    additionalNote: '',
    cabinCategories: [],
    preHotel: [] as any[],
    postHotel: [] as any[],
    flight: [] as any[],
    transfers: ''
  };
};
const cruiseTabs = reactive([initCruiseData()]);

const isAvancedForm = ref(true);
const isShowError = ref(false);
const isPreHotel = ref([false]);
const isPostHotel = ref([false]);
const isFlight = ref([false]);
const isTransfer = ref([false]);
const maxDateRangePreHotel = ref();
const maxDateRangePostHotel = ref();
const listFlightClass = ref<any[]>([]);
const listDestAir = ref([]);
const listAirline = ref([]);
const isInvalidDate = ref(false);
const isInvalidDuration = ref(false);
const showTableFlight = ref(false);
const isLoading = computed(() => submitQuoteStore.selectIsLoading);
const vendors = computed(() => cruiseStore.selectVendors);
const quote = computed(() => viewQuoteStore.selectQuote as any);
const ships = computed(() => cruiseStore.selectShips as any);
const sailingRequote = computed(() => {
  return cruiseStore.selectSailId;
});
const vendorId = computed(() => cruiseStore.selectListVenDor);

const lineById = computed(() => cruiseStore.selectLineById);
const shipById = computed(() => cruiseStore.selectShipById);
const destinationRequote = computed(() => destinationStore.selectDestinationRequote);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const destById = computed(() => cruiseStore.selectDestById);
const listDestRequote = computed(() => cruiseStore.selectListDestRequote);
const cruiseOptionConfig = computed(() => (adminSetupStore.optionConfig as any)?.cruiseOptionConfig);
const groupDestination = computed(() => cruiseStore.selectGroupDestination);
const listAirlineStore = computed(() => airManagementStore.airs);
const cruiseMinimumConfig = computed(() => adminSetupStore.minimumConfig?.cruiseConfig);
const advancedRules = computed(() => {
  return {
    $each: helpers.forEach({
      destination: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      lineName: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      sailDate: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      duration: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      shipName: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      sailingName: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      numberOfCabins: {
        required: helpers.withMessage(t('validate.required'), required),
        integer
      },
      adults: {
        required: helpers.withMessage(t('validate.required'), required),
        minValue: helpers.withMessage(t('validate.required'), minValue(1))
      },
      cabinCategories: {
        required: helpers.withMessage(t('validate.required'), required)
      },
      preHotel: {
        $each: helpers.forEach({
          destination: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          dateRange: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          numOfRooms: {
            required: helpers.withMessage(t('validate.required'), required),
            minValue: helpers.withMessage(t('validate.required'), minValue(1))
          },
          adults: {
            required: helpers.withMessage(t('validate.required'), required),
            minValue: helpers.withMessage(t('validate.required'), minValue(1))
          },
          hotel: {
            $each: helpers.forEach({
              hotelName: {
                required: helpers.withMessage(t('validate.required'), required)
              },
              rooms: {
                $each: helpers.forEach({
                  room: {
                    required: helpers.withMessage(t('validate.required'), required)
                  }
                })
              }
            })
          }
        })
      },
      postHotel: {
        $each: helpers.forEach({
          destination: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          dateRange: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          adults: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          numOfRooms: {
            required: helpers.withMessage(t('validate.required'), required),
            minValue: helpers.withMessage(t('validate.required'), minValue(1))
          },
          hotel: {
            $each: helpers.forEach({
              hotelName: {
                required: helpers.withMessage(t('validate.required'), required)
              },
              rooms: {
                $each: helpers.forEach({
                  room: {
                    required: helpers.withMessage(t('validate.required'), required)
                  }
                })
              }
            })
          }
        })
      },
      flight: {
        $each: helpers.forEach({
          gateway: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          destination: {
            required: helpers.withMessage(t('validate.required'), required)
          },
          dateRange:
            isRoundtrip.value && isOpenJaw.value
              ? {
                  required: helpers.withMessage(t('validate.required'), required)
                }
              : null,
          depDate: isOneWay.value
            ? {
                required: helpers.withMessage(t('validate.required'), required)
              }
            : null,
          adults: {
            required
          },
          returningFrom: isOpenJaw.value
            ? {
                required: helpers.withMessage(t('validate.required'), required)
              }
            : null,
          returningTo: isOpenJaw.value
            ? {
                required: helpers.withMessage(t('validate.required'), required)
              }
            : null
        })
      }
    })
  };
});

const getDestAir = async () => {
  const response = await cityStore.getDestAir();
  listDestAir.value = response;
};
let v$ = useVuelidate(advancedRules, cruiseTabs as any);

const formatISODate = (date: any, isLocal: any) => {
  return formatDate(date, 'yyyy-LL-dd HH:mm', isLocal).replace(' ', 'T');
};

const checkPreHotel = (formType: any, value: any, cruiseIndex: number) => {
  if (formType) {
    if (isPreHotel.value[cruiseIndex]) {
      return value;
    } else {
      return [];
    }
  } else {
    return [];
  }
};

const checkPostHotel = (formType: any, value: any, cruiseIndex: number) => {
  if (formType) {
    if (isPostHotel.value[cruiseIndex]) {
      return value;
    } else {
      return [];
    }
  } else {
    return [];
  }
};

const checkAir = (formType: any, value: any, cruiseIndex: number) => {
  if (formType) {
    if (isFlight.value[cruiseIndex]) {
      return value;
    } else {
      return [];
    }
  } else {
    return [];
  }
};

const checkTranfer = (formType: any, value: any, cruiseIndex: number) => {
  if (formType) {
    if (isTransfer.value[cruiseIndex]) {
      return value;
    } else {
      return '';
    }
  } else {
    return '';
  }
};

const validateRoomsNumber = () => {
  let valid = true;
  if (cruiseTabs.some((cruise) => +cruise.numberOfCabins < cruiseMinimumConfig.value.minRoomsCabins)) {
    return false;
  }
  valid = validateHotelRoomDataCruise(cruiseTabs);
  return valid;
};

const validatePaxNumber = () => {
  let valid = true;
  if (cruiseTabs.some((cruise) => +cruise.adults + +cruise.children < cruiseMinimumConfig.value.minPax)) {
    return false;
  }
  valid = validateHotelPaxDataCruise(cruiseTabs);
  return valid;
};

const checkHotelInBlackoutDate = () => {
  return cruiseTabs.some((cruiseTab) => {
    const { postHotel = [], preHotel = [] } = cruiseTab;
    const tmpHotels = [...preHotel, ...postHotel].flatMap((hotelItem) => hotelItem.hotel);
    return !validateBlackoutDate(tmpHotels.map((hotel) => hotel.hotelName));
  });
};

const validateHotelPaxDataCruise = (cruiseData: any[]) => {
  const isInvalid = cruiseData.some((cruise: any, index: number) => {
    if (isPostHotel.value[index] && cruise?.postHotel?.some((hotel: any) => hotel.adults + hotel.children < cruiseMinimumConfig.value.minPax)) {
      return true;
    }
    if (isPreHotel.value[index] && cruise?.preHotel?.some((hotel: any) => hotel.adults + hotel.children < cruiseMinimumConfig.value.minPax)) {
      return true;
    }
    if (isFlight.value[index] && cruise?.flight?.some((flight: any) => +flight.adults + +flight.businessAdults + +flight.children + +flight.businessChildren < cruiseMinimumConfig.value.minPax)) {
      return true;
    }

    return false;
  });
  return !isInvalid;
};

const validateHotelRoomDataCruise = (cruiseData: any[]) => {
  const isInvalid = cruiseData.some((cruise: any, index: number) => {
    if (isPostHotel.value[index] && cruise?.postHotel?.some((hotel: any) => hotel.numOfRooms < cruiseMinimumConfig.value.minRoomsCabins)) {
      return true;
    }
    if (isPreHotel.value[index] && cruise?.preHotel?.some((hotel: any) => hotel.numOfRooms < cruiseMinimumConfig.value.minRoomsCabins)) {
      return true;
    }

    return false;
  });
  return !isInvalid;
};

const openModalTimeOut = () => {
  clearAllDataCruise();
  dialog.open(SubmitNotificationModal, {
    props: {
      header: t('advance_quote.confirm_quote_id'),
      modal: true
    }
  });
};

const getListAirline = () => {
  adminQuoteStore.getAirlines().then((res) => {
    listAirline.value = res;
  });
};
getListAirline();

const renderNameAirline = (code: string, showCountry?: boolean) => {
  const airport = listAirlineStore?.value?.find((air: any) => air.airportCode === code);
  let countryPart = '';
  if (showCountry && airport?.countryName) {
    countryPart = ` (${airport.countryName})`;
  }
  return `${airport?.airportName}${countryPart}`;
};

function renderNameAirlineFr(code: any, showCountry?: boolean) {
  const airport = listAirlineStore?.value?.find((air: any) => air.airportCode === code);
  let countryPart = '';
  if (showCountry && airport?.countryNameFr) {
    countryPart = ` (${airport.countryNameFr})`;
  }
  return `${airport?.airportNameFR}${countryPart}`;
}

const getFlightPayload = (cruise: any, cruiseIndex: number) => {
  const result = listFlightClass.value[cruiseIndex].map((item: any) => {
    return {
      type: item.type,
      depTime: item.depTime,
      arrTime: item.arrivalTime,
      flightNo: item.flight,
      gateCode: item.depAirport,
      chd: parseInt(cruise?.flight[0]?.children, 10) ?? 0,
      adt: parseInt(cruise?.flight[0]?.adults) ?? 0,
      businessChd: parseInt(cruise?.flight[0]?.businessChildren, 10) ?? 0,
      businessAdt: parseInt(cruise?.flight[0]?.businessAdults) ?? 0,
      isEconomy: cruise?.flight[0]?.isEconomy,
      isBusiness: cruise?.flight[0]?.isBusiness,
      gate: renderNameAirline(item.depAirport),
      gateFr: renderNameAirlineFr(item.depAirport),
      destCode: item.arrivalAirport,
      dest: renderNameAirline(item.arrivalAirport, true),
      destFr: renderNameAirlineFr(item.arrivalAirport, true),
      depDate: formatDate(item.depDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      arrDate: formatDate(item.arrivalDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      airline: listAirline.value?.find((air) => air.code === item.operated)?.name,
      airlineCode: item.operated,
      flightNumber: item.flightNumber,
      operated: item.operated,
      classesAvailable: item.classesAvailable,
      via: '',
      fClass: ''
    };
  });
  return result;
};

const checkAddFlightSubmit = () => {
  isShowError.value = true;
  v$.value.$validate();
  if (!v$.value.$error) {
    const flightNotSelected = cruiseTabs.find((_, cruiseIndex) => {
      return isFlight.value[cruiseIndex] && !listFlightClass.value?.[cruiseIndex]?.length;
    });

    if (flightNotSelected) {
      openAddFlightConfirmDialog();
    } else {
      handleSubmit();
    }
  }
};

const validateFormCruiseAdvanced = () => {
  let isValidateAdvanced = true;

  cruiseTabs.forEach((cruiseTab: any, cruiseIndex: number) => {
    if (isFlight.value[cruiseIndex]) {
      cruiseTab.flight.forEach((flight: any) => {
        if (flight.tripTypeSelected === ROUNDTRIP && (!flight.dateRange[0] || !flight.dateRange[1])) {
          isValidateAdvanced = false;
        }
        if (flight.tripTypeSelected === OPENJAW && (!flight.dateRange[0] || !flight.dateRange[1] || !flight.returningFrom || !flight.returningTo)) {
          isValidateAdvanced = false;
        }
        if (flight.tripTypeSelected === ONEWAY && !flight.depDate) {
          isValidateAdvanced = false;
        }
      });
    }
  });
  return isValidateAdvanced;
};

const formatHotel = (data: any, option: any) => {
  return data.hotel.map((hotelItem: any) => {
    return {
      ...option,
      hotel: hotelItem.hotelName.name,
      hCode: hotelItem.hotelName.hCode,
      hotelChain: hotelItem.hotelName.chainName,
      code: hotelItem.hotelName.code,
      adminMaxRoom: hotelItem.hotelName.maxRooms,
      rooms: hotelItem.rooms.map((hotelRoom: any) => ({
        room: renderRoomName(hotelRoom.room, LANG_ENGLISH, false),
        roomFr: renderRoomName(hotelRoom.room, LANG_FRENCH, false),
        longName: renderRoomName(hotelRoom.room, LANG_ENGLISH, true),
        longNameFr: renderRoomName(hotelRoom.room, LANG_FRENCH, true),
        code: hotelRoom.room.room,
        totalTax: 0
      }))
    };
  });
};

const removeTag = (cruiseArr: any) => {
  const cruiseList = cloneDeep(cruiseArr);
  cruiseList.forEach((cruise: any) => {
    delete cruise.displayTag;
  });
  return cruiseList;
};

const extractPreHotels = (cruise: any) => {
  const preTemp: any[] = [];
  cruise?.preHotel?.forEach((pre: any) => {
    const preOption = {
      depDate: formatISODate(pre.dateRange[0], true),
      retDate: formatISODate(pre.dateRange[1], true),
      dest: pre.destination?.name,
      destCode: pre.destination?.code,
      airportCode: pre.destination?.airportCode,
      adt: +pre.adults,
      numOfRooms: +pre.numOfRooms,
      chd: parseInt(pre.children, 10) ?? 0,
      duration: pre.duration
    };

    const preHotelTmp = {
      ...preOption,
      hotels: formatHotel(pre, preOption)
    };

    preTemp.push(preHotelTmp);
  });
  return preTemp;
};

const extractPostHotels = (cruise: any) => {
  const postTemp: any[] = [];

  cruise?.postHotel?.forEach((post: any) => {
    const postOption = {
      depDate: formatISODate(post.dateRange[0], true),
      retDate: formatISODate(post.dateRange[1], true),
      dest: post.destination?.name,
      destCode: post.destination?.code,
      airportCode: post.destination?.airportCode,
      adt: +post.adults,
      numOfRooms: +post.numOfRooms,
      chd: parseInt(post.children, 10) ?? 0,
      duration: post.duration
    };

    const postHotelTmp = {
      ...postOption,
      hotels: formatHotel(post, postOption)
    };

    postTemp.push(postHotelTmp);
  });
  return postTemp;
};

const extractFlights = (cruise: any, cruiseIndex: number) => {
  let flightTemp: any[] = [];

  if (isFlight.value[cruiseIndex]) {
    flightTypesOption.forEach((el, index) => {
      const flightOption = {
        type: el.type,
        gate: index === 0 ? cruise?.flight?.[0]?.gateway?.dataSelected : cruise?.flight?.[0]?.destination?.dataSelected,
        gateCode: index === 0 ? cruise?.flight?.[0]?.gateway.airportCode : cruise?.flight?.[0]?.destination.airportCode,
        depDate: index === 0 ? formatISODate(cruise?.flight?.[0]?.dateRange[0] ?? cruise?.flight?.[0]?.depDate, false) : formatISODate(cruise?.flight?.[0]?.dateRange[1], true),
        dest: index === 0 ? cruise?.flight?.[0]?.destination?.dataSelected : cruise?.flight?.[0]?.gateway?.dataSelected,
        destCode: index === 0 ? cruise?.flight?.[0]?.destination.airportCode : cruise?.flight?.[0]?.gateway.airportCode,
        airline: '',
        airlineCode: '',
        flightNo: '',
        fClass: '',
        arrDate: null,
        via: '',
        adt: cruise?.flight?.[0]?.adults,
        chd: cruise?.flight?.[0]?.children ?? 0,
        direct: !!cruise?.flight?.[0]?.directFlight,
        businessAdt: cruise?.flight?.[0]?.businessAdults,
        businessChd: cruise?.flight?.[0]?.businessChildren ?? 0,
        isEconomy: cruise?.flight?.[0]?.isEconomy,
        isBusiness: cruise?.flight?.[0]?.isBusiness
      };
      flightTemp.push(flightOption);
    });
    if (listFlightClass.value?.[cruiseIndex]?.length) {
      flightTemp = getFlightPayload(cruise, cruiseIndex);
    }
  }

  return flightTemp;
};

const getDestinationInfo = async (cruise: any) => {
  let destinationName;
  let destinationId;
  const destinationSelected = Object.keys(cruise.destination)[0];
  const filter = groupDestination.value?.filter((f: any) => f.key.toString() === destinationSelected) as any[];
  if (!filter?.length) {
    groupDestination.value?.forEach((f: any) => {
      f.children?.forEach((c: any) => {
        if (c.key === destinationSelected) {
          cruiseStore.updateDestinationsCruiseSelectedId(c.value);
          if (c.value) {
            destinationId = c.value;
            destinationName = c.label;
          }
        }
        c.children?.forEach((gc) => {
          if (gc.key === destinationSelected) {
            cruiseStore.updateDestinationsCruiseSelectedId(gc.value);
            if (gc.value) {
              destinationId = gc.value;
              destinationName = gc.label;
            }
          }
        });
      });
    });
  } else if (filter[0].value) {
    destinationId = filter[0].value;
    destinationName = filter[0].label;
  }

  return { destinationId, destinationName };
};

const showValidationMessageCruiseQuoteForm = (message: string) => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    props: {
      modal: true,
      header: t('advance_quote.form.notice'),
      style: {
        maxWidth: '90vw'
      }
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.return_to_form'),
              autofocus: true,
              onClick: () => dialogRef.close()
            })
          )
        ];
      }
    },
    data: {
      message: message
    }
  });
};

const handleSubmit = async () => {
  await emits('submit');
  const cruiseTemp: any[] = [];

  isShowError.value = true;
  v$.value.$validate();

  await Promise.all(
    cruiseTabs.map(async (cruise: any, cruiseIndex: number) => {
      const preTemp = extractPreHotels(cruise);
      const postTemp = extractPostHotels(cruise);
      const flightTemp = extractFlights(cruise, cruiseIndex);

      const [depPortRes, rePortRes, destInfo] = await Promise.all([cruiseStore.getRePortId(cruise.sailingName.portId), cruiseStore.getRePortId(cruise.sailingName.retPortId), getDestinationInfo(cruise)]);
      const { destinationId, destinationName } = destInfo;
      const formCruiseFlight = cruise?.flight?.[0] ?? initFlight()[0];

      const formCruise = {
        sailing: cruise.sailingName.sail,
        destId: destinationId,
        lineId: cruise.lineName.id,
        shipId: cruise.shipName.id,
        sailingId: cruise.sailingName.sailId,
        gate: formCruiseFlight?.gateway?.airportNameEn,
        gateFr: formCruiseFlight?.gateway?.airportNameFr,
        itinerary: formCruiseFlight?.tripTypeSelected,
        dest: destinationName,
        cruiseLine: cruise.lineName.name,
        ship: cruise.shipName.name,
        sailDate: DateTime.fromISO(new Date(cruise.sailingName.dep).toISOString(), { zone: 'UTC' }).toFormat('yyyy-LL-dd'),
        retDate: DateTime.fromISO(new Date(cruise.sailingName.dep).toISOString(), { zone: 'UTC' }).plus({ days: cruise.sailingName.dur }).toFormat('yyyy-LL-dd'),
        duration: +cruise.duration,
        cabinNo: +cruise.numberOfCabins,
        cabinCategory: removeTag(cruise.cabinCategories),
        adt: +cruise.adults,
        chd: parseInt(cruise.children, 10) ?? 0,
        dining: cruise.diningPreference,
        depPort: depPortRes?.data?.Name,
        flightPrices: isAvancedForm.value && isFlight.value ? serviceFlight : [],
        retPort: rePortRes?.data?.Name,
        portOfCall: rePortRes?.data?.Name,
        imgUrl: cruise.shipName.Image,
        additionalNote: cruise.additionalNote,
        preHotel: checkPreHotel(isAvancedForm.value, preTemp, cruiseIndex),
        postHotel: checkPostHotel(isAvancedForm.value, postTemp, cruiseIndex),
        flights: checkAir(isAvancedForm.value, flightTemp, cruiseIndex),
        transfers: checkTranfer(isAvancedForm.value, cruise.transfers, cruiseIndex),
        returnFrom: formCruiseFlight?.returningFrom?.airportNameEn ?? '',
        returnFromFr: formCruiseFlight?.returningFrom?.airportNameFr ?? '',
        returnFromCode: formCruiseFlight?.returningFrom?.airportCode ?? '',
        returnTo: formCruiseFlight?.returningTo?.airportNameEn ?? '',
        returnToFr: formCruiseFlight?.returningTo?.airportNameFr ?? '',
        returnToCode: formCruiseFlight?.returningTo?.airportCode ?? ''
      };
      cruiseTemp.push(formCruise);
    })
  );

  if (!v$.value.$error && validateFormCruiseAdvanced()) {
    if (props.isValidation) {
      if ((!validatePaxNumber() || !validateRoomsNumber()) && !isWorksheetRequote.value) {
        return showValidationMessageCruiseQuoteForm(t('advance_quote.form.min_pax_cruise', { minPax: cruiseMinimumConfig.value.minPax, minRoom: cruiseMinimumConfig.value.minRoomsCabins }));
      }
      if (checkHotelInBlackoutDate()) {
        return showValidationMessageCruiseQuoteForm(t('advance_quote.form.has_black_out_period'));
      }

      const agentInfo = cloneDeep(isWorksheetRequote.value && !isClearAgencyInfo.value ? getUserQuote(quote.value) : getUserParam());
      const payload = {
        ...agentInfo,
        lang: props.selectedLanguage,
        gName: isAvancedForm.value ? props.advancedForm[0].groupName : props.instanceForm[0].groupName,
        gContact: isAvancedForm.value ? props.advancedForm[0].groupContact : props.instanceForm[0].groupContact,
        gType: isAvancedForm.value ? props.advancedForm[0].groupType : props.instanceForm[0].groupType,
        gTypeFr: isAvancedForm.value ? props.advancedForm[0].groupTypeFr : props.instanceForm[0].groupTypeFr,
        gTypeId: isAvancedForm.value ? props.advancedForm[0].groupTypeId : props.instanceForm[0].groupTypeId,
        qType: CRUISE,
        advanced: isAvancedForm.value,
        cruises: cruiseTemp,
        avgPaxRoom: props.avgPaxRoomData,
        originalQuote: renderOriginQuote(true, quote.value?.sk),
        isReplicate: isWorksheetRequote.value
      };

      submitQuoteStore
        .handleSubmit(payload, undefined, openModalTimeOut)
        .then(() => {
          dialog.open(SubmitNotificationModal, {
            props: {
              header: t('my_quote.quote_id'),
              modal: true
            }
          });
          clearDataCruise();
          showTableFlight.value = false;
        })
        .catch((e) => {
          toast.add({ severity: 'error', summary: '', detail: e.message || t('notification.save_error'), life: 5000 });
        });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
};

const clearDataCruise = async () => {
  cruiseTabs.splice(0);

  await nextTick();

  cruiseTabs.push(initCruiseData());

  isPreHotel.value = Array(cruiseTabs.length).fill(false);
  isPostHotel.value = Array(cruiseTabs.length).fill(false);
  isTransfer.value = Array(cruiseTabs.length).fill(false);
  isFlight.value = Array(cruiseTabs.length).fill(false);
  hotelStore.clearRoomCategory();
  viewQuoteStore.updateViewQuote({});
  isShowError.value = false;
  viewQuoteStore.getAction('view');
  viewQuoteStore.getActionRequoteFromWorksheet(true);
};

const clearAllDataCruise = async () => {
  await clearDataCruise();
  clearSelectedFlight();
  maxDateRangePostHotel.value = undefined;
  maxDateRangePreHotel.value = undefined;
  emits('clear');
  isInvalidDate.value = false;
  isInvalidDuration.value = false;
  isClearAgencyInfo.value = true;
};
const clearSelectedFlight = () => {
  listFlightClass.value = [];
  showTableFlight.value = false;
};

const openAddFlightConfirmDialog = () => {
  const dialogRef = dialog.open(AddFlightConfirmModal, {
    props: {
      header: t('create_quote.flight_information'),
      modal: true
    },
    emits: {
      onConfirm: () => {
        dialogRef.close();
        handleSubmit();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const renderQuoteTabIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-term', innerHTML: props.quoteIntro.termsFr });
  } else {
    return h('div', { class: 'english-term', innerHTML: props.quoteIntro.terms });
  }
};

const setHotelOption = (hotelsArray: any, cruiseIndex: number, typeIndex: number, hotelType: 'preHotel' | 'postHotel') => {
  hotelsArray.hotels.forEach((hotel: any, hotelIndex: number) => {
    cruiseTabs[cruiseIndex][hotelType][typeIndex].hotel.push(initHotelOption());
    cruiseTabs[cruiseIndex][hotelType][typeIndex].hotel[hotelIndex].rooms = [];

    hotel.rooms?.forEach(() => {
      cruiseTabs[cruiseIndex][hotelType][typeIndex].hotel[hotelIndex].rooms.push({
        room: ''
      });
    });
  });
};

const setPreHotel = () => {
  isPreHotel.value = Array(quote.value.cruises.length).fill(false);

  quote.value.cruises.forEach((cruise: any, cruiseIndex: number) => {
    if (cruise.preHotel.length > 0) {
      cruise.preHotel.forEach(async (pre: any, index: number) => {
        cruiseTabs[cruiseIndex].preHotel[index].hotel = [];
        if (isRequote.value) {
          isPreHotel.value[cruiseIndex] = true;
          await destinationStore.getDestinationRequote(pre.destCode);
          await destinationStore.getDurationRequoteByDestination(pre.duration, destinationRequote.value[0]);
          cruiseTabs[cruiseIndex].preHotel[index].destination = destinationStore.selectDestinationRequote[0];
          cruiseTabs[cruiseIndex].preHotel[index].adults = pre.adt;
          cruiseTabs[cruiseIndex].preHotel[index].numOfRooms = pre.numOfRooms;
          cruiseTabs[cruiseIndex].preHotel[index].children = pre.chd;
          setHotelOption(pre, cruiseIndex, index, 'preHotel');
        }
      });
    }
  });
};

const setPostHotel = () => {
  isPostHotel.value = Array(quote.value.cruises.length).fill(false);

  quote.value.cruises.forEach((cruise: any, cruiseIndex: number) => {
    if (cruise.postHotel.length > 0) {
      cruise.postHotel.forEach(async (post: any, index: number) => {
        cruiseTabs[cruiseIndex].postHotel[index].hotel = [];
        if (isRequote.value) {
          isPostHotel.value[cruiseIndex] = true;
          await destinationStore.getDestinationRequote(post.destCode);
          await destinationStore.getDurationRequoteByDestination(post.duration, destinationRequote.value[0]);
          cruiseTabs[cruiseIndex].postHotel[index].destination = destinationStore.selectDestinationRequote[0];
          cruiseTabs[cruiseIndex].postHotel[index].adults = post.adt;
          cruiseTabs[cruiseIndex].postHotel[index].numOfRooms = post.numOfRooms;
          cruiseTabs[cruiseIndex].postHotel[index].children = post.chd;
          setHotelOption(post, cruiseIndex, index, 'postHotel');
        }
      });
    }
  });
};

const setDirectFlight = () => {
  if (!isRequote.value) {
    return;
  }

  isFlight.value = Array(quote.value.cruises.length).fill(false);
  quote.value.cruises.forEach((cruise: any, cruiseIndex: number) => {
    if (cruise.flights?.length) {
      isFlight.value[cruiseIndex] = true;
    }

    cruise.flights?.forEach(async (flight: any, index: number) => {
      if (cruiseTabs[cruiseIndex].flight[index]) {
        cruiseTabs[cruiseIndex].flight[index].gateway = cityStore.getAirportCityDetail(flight.gateCode);
        cruiseTabs[cruiseIndex].flight[index].destination = cityStore.getAirportCityDetail(flight.destCode);
        cruiseTabs[cruiseIndex].flight[index].adults = flight.adt;
        cruiseTabs[cruiseIndex].flight[index].children = flight.chd;
        cruiseTabs[cruiseIndex].flight[index].directFlight = flight.direct;
        cruiseTabs[cruiseIndex].flight[index].businessAdults = flight.businessAdt;
        cruiseTabs[cruiseIndex].flight[index].businessChildren = flight.businessChd;
        cruiseTabs[cruiseIndex].flight[index].isEconomy = flight.isEconomy;
        cruiseTabs[cruiseIndex].flight[index].isBusiness = flight.isBusiness;
      }
    });
  });
};

const setTransfer = () => {
  if (!isRequote.value) {
    return;
  }
  isTransfer.value = Array(quote.value.cruises.length).fill(false);
  quote.value.cruises.forEach((cruise: any, cruiseIndex: number) => {
    cruiseTabs[cruiseIndex].transfers = cruise.transfers;
    if (cruise.transfers) {
      isTransfer.value[cruiseIndex] = true;
    }
  });
};

const checkDataConfigOption = (maxHotel: number, maxRoom: number, maxCategory: number) => {
  const isHotelConfigRemain = maxHotel === maxHotelConfig.value;

  cruiseTabs.forEach((cruise: any) => {
    removeFromEnd(cruise.cabinCategories, maxCategory);
    if (isHotelConfigRemain) {
      return;
    }
    if (cruise.preHotel.length) {
      cruise.preHotel.forEach((pre: any) => {
        removeFromEnd(pre.hotel, maxHotel);
        pre.hotel.forEach((hotel: any) => {
          removeFromEnd(hotel.rooms, maxRoom);
        });
      });
    }
    if (cruise.postHotel.length) {
      cruise.postHotel.forEach((post: any) => {
        removeFromEnd(post.hotel, maxHotel);
        post.hotel.forEach((hotel: any) => {
          removeFromEnd(hotel.rooms, maxRoom);
        });
      });
    }
  });
};

const handleClear = (maxOption: number, maxHotel: number, maxRoom: number, maxCategory: number) => {
  if (!maxOptionConfig.value) {
    return;
  }

  if (maxOption !== maxOptionConfig.value) {
    removeFromEnd(cruiseTabs, maxOption);
  }
  checkDataConfigOption(maxHotel, maxRoom, maxCategory);
};

const extractCruiseOptionSize = () => {
  const cruiseOtionLength = quote.value.cruises?.length ?? 1;
  let cruiseMaxHotelLength = 1;
  let cruiseMaxRoomLength = 1;
  let cruiseMaxCabinCategory = 1;

  const findMaxRoomLength = (hotels: any[]) => {
    hotels.forEach((hotel) => {
      cruiseMaxRoomLength = max([cruiseMaxRoomLength, hotel.rooms?.length ?? 1]);
    });
  };

  quote.value.cruises?.forEach((cruise: any) => {
    cruiseMaxCabinCategory = max([cruiseMaxCabinCategory, cruise.cabinCategory?.length ?? 1]);

    cruise.preHotel.forEach((hotel: any) => {
      cruiseMaxHotelLength = max([cruiseMaxHotelLength, hotel.hotels?.length ?? 1]);
      findMaxRoomLength(hotel.hotels);
    });
    cruise.postHotel.forEach((hotel: any) => {
      cruiseMaxHotelLength = max([cruiseMaxHotelLength, hotel.hotels?.length ?? 1]);
      findMaxRoomLength(hotel.hotels);
    });
  });

  return { cruiseOtionLength, cruiseMaxHotelLength, cruiseMaxRoomLength, cruiseMaxCabinCategory };
};

const handleChangeGroupType = (advancedForm: any, config: any) => {
  if (advancedForm[0].groupType) {
    let { maxHotel, maxOption, maxRoom, maxCruiseCategory } = handleSetLimit(advancedForm, config);

    if (isRequote.value && isWorksheetRequote.value) {
      const { cruiseOtionLength, cruiseMaxHotelLength, cruiseMaxRoomLength, cruiseMaxCabinCategory } = extractCruiseOptionSize();
      maxOption = max([maxOption, cruiseOtionLength])!;
      maxHotel = max([maxHotel, cruiseMaxHotelLength])!;
      maxRoom = max([maxRoom, cruiseMaxRoomLength])!;
      maxCruiseCategory = max([maxCruiseCategory, cruiseMaxCabinCategory])!;
    }

    handleClear(maxOption, maxHotel, maxRoom, maxCruiseCategory);
    maxOptionConfig.value = maxOption;
    maxHotelConfig.value = maxHotel;
    maxRoomConfig.value = maxRoom;
    maxCategoryConfig.value = maxCruiseCategory;
  }
};

const addOptionCruise = () => {
  cruiseTabs.push(initCruiseData());
  isPreHotel.value.push(false);
  isPostHotel.value.push(false);
  isFlight.value.push(false);
  isTransfer.value.push(false);
  listFlightClass.value.push([]);

  const lastCruiseTabIndex = cruiseTabs.length - 1;
  cruiseTabs[lastCruiseTabIndex].destination = cruiseTabs[0].destination;
  cruiseTabs[lastCruiseTabIndex].lineName = cruiseTabs[0].lineName;
  cruiseTabs[lastCruiseTabIndex].shipName = cruiseTabs[0].shipName;
  maxDateRangeValue.value[lastCruiseTabIndex] = isWorksheetRequote.value ? undefined : getLimitDate(props.advancedForm[0].groupTypeId, limitCalendar.value, 'endDateConfig');
};

const removeOptionCruise = () => {
  cruiseTabs.pop();
  isPreHotel.value.pop();
  isPostHotel.value.pop();
  maxDateRangeValue.value.pop();
  isFlight.value.pop();
  isTransfer.value.pop();
  listFlightClass.value.pop();
  handleAssignValidateValues();
};

const handleAssignValidateValues = async () => {
  v$ = useVuelidate(advancedRules, cruiseTabs as any);
};

const addRoom = () => {
  handleAssignValidateValues();
};

const removeRoom = () => {
  handleAssignValidateValues();
};

const addHotel = () => {
  handleAssignValidateValues();
};

const removeHotel = () => {
  handleAssignValidateValues();
};

const addPostHotel = (value: boolean, index: number) => {
  if (value) {
    cruiseTabs[index].postHotel = initHotel();
  } else {
    cruiseTabs[index].postHotel = [];
  }
  handleAssignValidateValues();
};

const addPreHotel = (value: boolean, index: number) => {
  if (value) {
    cruiseTabs[index].preHotel = initHotel();
  } else {
    cruiseTabs[index].preHotel = [];
  }
  handleAssignValidateValues();
};

const addFlight = (value: boolean, index: number) => {
  if (value) {
    cruiseTabs[index].flight = initFlight();
  } else {
    cruiseTabs[index].flight = [];
  }
  handleAssignValidateValues();
};

const addTransfer = () => {
  handleAssignValidateValues();
};

const onUpdateDestination = (value: any, index: number) => {
  if (index !== 0) {
    return;
  }

  cruiseTabs.forEach((cruiseTab, cruiseTabIndex) => {
    if (cruiseTabIndex === 0) {
      return;
    }
    cruiseTab.destination = value;
    cruiseTab.lineName = '';
    cruiseTab.shipName = '';
    cruiseTab.sailingName = '';
  });
};

const onUpdateLineName = (value: any, index: number) => {
  if (index !== 0) {
    return;
  }

  cruiseTabs.forEach((cruiseTab, cruiseTabIndex) => {
    if (cruiseTabIndex === 0) {
      return;
    }
    cruiseTab.lineName = value;
    cruiseTab.shipName = '';
    cruiseTab.sailingName = '';
  });
};

const onUpdateShipName = (value: any, index: number) => {
  if (index !== 0) {
    return;
  }

  cruiseTabs.forEach((cruiseTab, cruiseTabIndex) => {
    if (cruiseTabIndex === 0) {
      return;
    }
    cruiseTab.shipName = value;
  });

  listCabinCategory.value = getListCabins(value.cabins, false);
};

const getListCabins = (cabins: any, isCheckConfig: boolean) => {
  const result: any[] = [];
  if (isCheckConfig && isRequote.value && !isWorksheetRequote.value) {
    removeFromEnd(cabins, cruiseOptionConfig.value?.cruiseMaxCategory);
  }
  cabins?.forEach((cabin: any) => {
    cabin.displayTag = getTextVariant(currentLanguage, cabin.nameEn, cabin.nameFr) + ' - ' + cabin.code;
    result.push(cabin);
  });
  return result;
};

const handleDateLimitationCruise = (advancedFormCruise: any) => {
  if (advancedFormCruise) {
    minDateRangeValue.value = isWorksheetRequote.value ? new Date(DateTime.utc().plus({ days: 1 })) : getLimitDate(advancedFormCruise, limitCalendar.value, 'startDateConfig');
    maxDateRangeValue.value[0] = isWorksheetRequote.value ? undefined : getLimitDate(advancedFormCruise, limitCalendar.value, 'endDateConfig');
  }
};

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      handleChangeGroupType(newValue, cruiseOptionConfig.value);
      handleDateLimitationCruise(newValue[0].groupTypeId);
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => cruiseTabs,
  (newValue) => {
    newValue.forEach((cruise, index) => {
      if (!cruise.adults) {
        cruise.adults = 0;
      }
      if (!cruise.children) {
        cruise.children = 0;
      }
      cruise.flight.forEach((flight, flightIndex) => {
        if (!flight.adults) {
          cruiseTabs[index].flight[flightIndex].adults = 0;
        }
        if (!flight.children) {
          cruiseTabs[index].flight[flightIndex].children = 0;
        }
        if (!flight.businessAdults) {
          cruiseTabs[index].flight[flightIndex].businessAdults = 0;
        }
        if (!flight.businessChildren) {
          cruiseTabs[index].flight[flightIndex].businessChildren = 0;
        }
      });
    });
  },
  { deep: true }
);

watch(
  () => cruiseTabs,
  (newVal) => {
    newVal.forEach((cruise) => {
      cruise.flight.forEach((flight: any) => {
        if (flight.tripTypeSelected === ROUNDTRIP) {
          isRoundtrip.value = true;
          isOpenJaw.value = false;
          isOneWay.value = false;
        }
        if (flight.tripTypeSelected === OPENJAW) {
          isRoundtrip.value = false;
          isOpenJaw.value = true;
          isOneWay.value = false;
        }
        if (flight.tripTypeSelected === ONEWAY) {
          isRoundtrip.value = false;
          isOpenJaw.value = false;
          isOneWay.value = true;
        }
      });
    });
  },
  { deep: true, immediate: true }
);

watch(
  () => props.calendarData,
  (newValue) => {
    if (newValue) {
      limitCalendar.value = getQuoteDepartureData(newValue);
    }
  },
  { deep: true }
);

const updateCruiseTabs = async (index: number, data: any) => {
  const { destId, lineId, shipId, duration, cabinNo, chd, adt, additionalNote, dining, cabinCategory, sailingId, returnFromCode, returnToCode, itinerary, transfers } = data;
  if (isRequote.value) {
    cruiseStore.getDestinationCruise(destId);
    const destination = destById.value.length ? destById.value[0].key : destById.value.key;
    cruiseTabs[index].destination = { [destination]: true };
    if (listDestRequote.value.length) {
      await cruiseStore.getNewVendors(listDestRequote.value);
    }
    if (vendors.value) {
      cruiseStore.getVendorById(vendors.value, lineId);
    }
    cruiseTabs[index].lineName = lineById.value[0];
    cruiseStore.updateVendorIdRequote(lineById.value[0].vendors, lineById.value[0].id);
    if (vendorId.value) {
      await cruiseStore.getShips(vendorId.value);
    }
    const listCabins = ships.value?.find((item: any) => item.id === shipId);
    listCabinCategory.value = getListCabins(listCabins?.cabins, false);
    cruiseStore.getShipById(shipId);
    cruiseTabs[index].shipName = shipById.value[0];
    cruiseStore.getSailingById(listDestRequote.value, lineId, shipId);
    cruiseTabs[index].duration = duration;
    cruiseTabs[index].numberOfCabins = cabinNo;
    cruiseTabs[index].children = chd;
    cruiseTabs[index].adults = adt;
    cruiseTabs[index].additionalNote = additionalNote;
    cruiseTabs[index].diningPreference = dining;
    cruiseTabs[index].transfers = transfers;
    if (cruiseTabs[index].flight[0]) {
      cruiseTabs[index].flight[0].tripTypeSelected = itinerary;
      cruiseTabs[index].flight[0].returningFrom = cityStore.getAirportCityDetail(returnFromCode);
      cruiseTabs[index].flight[0].returningTo = cityStore.getAirportCityDetail(returnToCode);
    }
    const newArr = getListCabins(cabinCategory, true);
    cruiseTabs[index].cabinCategories = newArr;
    const filterSailing = sailingRequote.value.filter((sail: any) => +sail.sailId === +sailingId);
    if (filterSailing.length > 0) {
      cruiseTabs[index].sailingName = filterSailing[0];
    }
  }
};

const checkDataConfigRemove = () => {
  removeFromEnd(quote.value.cruises, maxOptionConfig.value);
  quote.value.cruises.forEach((cruise: any) => {
    if (cruise.preHotel.length) {
      cruise.preHotel.forEach(async (pre: any) => {
        removeFromEnd(pre.hotels, maxHotelConfig.value);
        pre.hotels.forEach((hotel: any) => {
          removeFromEnd(hotel.rooms, maxRoomConfig.value);
        });
      });
    }
    if (cruise.postHotel.length) {
      cruise.postHotel.forEach(async (post: any) => {
        removeFromEnd(post.hotels, maxHotelConfig.value);
        post.hotels.forEach((hotel: any) => {
          removeFromEnd(hotel.rooms, maxRoomConfig.value);
        });
      });
    }
  });
  removeFromEnd(cruiseTabs, maxOptionConfig.value);
};

onMounted(async () => {
  handleAssignValidateValues();

  if (quote.value.cruises && quote.value.cruises.length > 0) {
    quote.value.cruises.map((q: any, index: number) => {
      if (index !== 0) {
        addOptionCruise();
      }
      q.preHotel.forEach(() => {
        cruiseTabs[index].preHotel.push(initHotel()[0]);
      });
      q.postHotel.forEach(() => {
        cruiseTabs[index].postHotel.push(initHotel()[0]);
      });
      if (q.flights.length) {
        cruiseTabs[index].flight.push(initFlight()[0]);
      }
    });

    await Promise.all(
      quote.value.cruises.map(async (q: any, index: number) => {
        await updateCruiseTabs(index, q);
      })
    );
    if (isRequote.value && !isWorksheetRequote.value) {
      checkDataConfigRemove();
    }
    setPreHotel();
    setPostHotel();
    setDirectFlight();
    setTransfer();
  }

  getDestAir();
});
</script>
<template>
  <div class="cruise-tab">
    <LoadingSpinner v-if="isLoading" />
    <div class="mb-3" v-if="quoteIntro">
      <renderQuoteTabIntroHtml></renderQuoteTabIntroHtml>
    </div>
    <div v-for="(cruiseTab, index) in cruiseTabs" :key="index">
      <CruiseFormOption
        v-model:is-show-error="isShowError"
        v-model:isFlight="isFlight[index]"
        v-model:is-pre-hotel="isPreHotel[index]"
        v-model:is-post-hotel="isPostHotel[index]"
        v-model:is-transfer="isTransfer[index]"
        v-model:listFlightClass="listFlightClass[index]"
        :cruise="cruiseTab"
        :indexOption="index"
        :validate="v$.$each.$response.$errors[index]"
        :advancedForm="advancedForm"
        :instanceForm="instanceForm"
        :calendarData="calendarData"
        :maxHotelConfig="maxHotelConfig"
        :maxRoomConfig="maxRoomConfig"
        :maxOptionConfig="maxOptionConfig"
        :maxCategoryConfig="maxCategoryConfig"
        :minCalendar="minDateRangeValue"
        :maxCalendar="maxDateRangeValue[index]"
        :listCabinCategory="listCabinCategory"
        @addRoom="addRoom()"
        @removeRoom="removeRoom()"
        @addHotel="addHotel()"
        @removeHotel="removeHotel()"
        @addPostHotel="(value) => addPostHotel(value, index)"
        @addPreHotel="(value) => addPreHotel(value, index)"
        @addFlight="(value) => addFlight(value, index)"
        @addTransfer="() => addTransfer()"
        @update-destination="(value) => onUpdateDestination(value, index)"
        @update-line-name="(value) => onUpdateLineName(value, index)"
        @update-ship-name="(value) => onUpdateShipName(value, index)"
      />
    </div>
    <div class="mt-2">
      <div class="flex w-full justify-content-around mb-3">
        <div v-if="cruiseTabs?.length > 1" href="javascript:" class="block text-primary cursor-pointer remove-option" @click="removeOptionCruise()">- {{ $t('advance_quote.form.remove_option') }}</div>
        <div v-if="maxOptionConfig && cruiseTabs?.length < maxOptionConfig" href="javascript:" class="block text-primary cursor-pointer add-option" @click="addOptionCruise()">+ {{ $t('advance_quote.form.add_option') }}</div>
      </div>
    </div>
    <div class="seperator mb-3 mt-5"></div>
    <div class="flex gap-3">
      <Button id="submit-btn" :disabled="isInvalidDuration || isInvalidDate" :label="$t('advance_quote.form.submit')" class="button-submit mt-2 bg-primary border-noround" @click="checkAddFlightSubmit()"></Button>
      <Button id="clear-btn" :label="$t('common.buttons.clear')" class="button-submit mt-2 bg-primary border-noround" @click="clearAllDataCruise()"></Button>
    </div>
    <p class="mt-3" v-if="!isAvancedForm">
      {{ $t('advance_quote.form.for_more_cruise') }} <a href="#/external/search/cruise" class="text-primary">{{ $t('advance_quote.form.click_here') }}</a
      >.
    </p>
    <Dialog v-model:visible="roomCategoryFetchingModalControl.visible" modal :header="roomCategoryFetchingModalControl.isFetching ? $t('advance_quote.searching_for_room') : $t('advance_quote.no_rooms_available')">
      <RoomCategoryFetchingModal :is-fetching="roomCategoryFetchingModalControl.isFetching" @closeModal="roomCategoryFetchingModalControl.visible = false"></RoomCategoryFetchingModal>
    </Dialog>
  </div>
</template>
